import { Flex } from "@redotech/redo-web/flex";
import { buildImageUrl } from "@redotech/redo-web/image-url";
import {
  ImageWithPlaceholder,
  type ImageWithPlaceholderProps,
} from "@redotech/redo-web/image-with-placeholder";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { LotBadge } from "./lot-badge";
import * as styles from "./wms-workflow.module.css";

const IMAGE_PIXELS = { sm: 40, md: 64, lg: 80, xl: 120 } as const;

interface ProductDisplayProps {
  imageUrl: string | null;
  productTitle: string;
  sku: string | null;
  lotNumber?: string | null;
  expirationDate?: string | null;
  imageSize?: "sm" | "md" | "lg" | "xl";
  /** Truncate the title instead of wrapping, so the row's height stays fixed. */
  singleLine?: boolean;
  clampTitle?: boolean;
  /** Drawn inside the empty image square when there is no image. */
  PlaceholderIcon?: ImageWithPlaceholderProps["DefaultIcon"];
}

export const ProductDisplay = memo(function ProductDisplay({
  imageUrl,
  productTitle,
  sku,
  lotNumber,
  expirationDate,
  imageSize = "xl",
  singleLine,
  clampTitle,
  PlaceholderIcon,
}: ProductDisplayProps) {
  const imageClass =
    imageSize === "sm"
      ? styles.productImageSm
      : imageSize === "md"
        ? styles.productImageMd
        : imageSize === "lg"
          ? styles.productImageLg
          : styles.productImageXl;

  const imagePixels = IMAGE_PIXELS[imageSize];
  const sizedImageUrl = useMemo(
    () => (imageUrl ? buildImageUrl(imageUrl, imagePixels, imagePixels) : null),
    [imageUrl, imagePixels],
  );

  const placeholderClass =
    imageSize === "sm"
      ? styles.productImagePlaceholderSm
      : imageSize === "md"
        ? styles.productImagePlaceholderMd
        : imageSize === "lg"
          ? styles.productImagePlaceholderLg
          : styles.productImagePlaceholderXl;

  return (
    <Flex align="center" gap="md" minW="none">
      {sizedImageUrl ? (
        <img
          alt={productTitle}
          className={imageClass}
          decoding="async"
          height={imagePixels}
          loading="lazy"
          src={sizedImageUrl}
          width={imagePixels}
        />
      ) : PlaceholderIcon ? (
        <ImageWithPlaceholder
          className={placeholderClass}
          DefaultIcon={PlaceholderIcon}
          image={null}
        />
      ) : (
        <div className={placeholderClass} />
      )}
      <Flex dir="column" gap="xxs" style={{ minWidth: 0 }}>
        <Text
          className={clampTitle ? styles.titleClamp : undefined}
          fontSize={imageSize === "sm" || imageSize === "md" ? "sm" : "md"}
          fontWeight="medium"
          overflow={singleLine ? "hidden" : undefined}
          textColor="primary"
          textOverflow={singleLine ? "ellipsis" : undefined}
          whiteSpace={singleLine ? "nowrap" : undefined}
        >
          {productTitle}
        </Text>
        {(sku || lotNumber) && (
          <Flex align="center" gap="xs">
            {sku && (
              <Text
                fontSize={
                  imageSize === "sm" || imageSize === "md" ? "xs" : "sm"
                }
                fontWeight="regular"
                textColor="secondary"
              >
                {sku}
              </Text>
            )}
            {lotNumber && (
              <LotBadge
                expirationDate={expirationDate}
                lotNumber={lotNumber}
                size="xs"
              />
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});
